import { useEffect } from "react";

// Read the per-request CSP nonce that server.mjs substitutes into the
// <meta name="csp-nonce"> tag. Required so dynamically-injected inline
// JSON-LD <script> tags satisfy the strict script-src nonce policy and
// are not blocked by the browser.
function getCspNonce(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const meta = document.querySelector('meta[name="csp-nonce"]');
  const value = meta?.getAttribute("content") ?? undefined;
  // In dev (Vite serves index.html as-is) the placeholder is not
  // substituted; treat it as absent so we don't set an invalid nonce.
  if (!value || value === "__CSP_NONCE__") return undefined;
  return value;
}

export function useJsonLd(id: string, data: unknown) {
  useEffect(() => {
    if (data === null || data === undefined) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    const nonce = getCspNonce();
    if (nonce) {
      script.setAttribute("nonce", nonce);
      // The `nonce` IDL attribute is the only reliable way to satisfy
      // CSP for scripts created via createElement in modern browsers.
      (script as HTMLScriptElement & { nonce?: string }).nonce = nonce;
    }
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
  }, [id, JSON.stringify(data)]);
}
